import { SocketResponse } from "../Types/SocketResponse"
import { getDataReviewPageRequestConcurrency } from "./DataReviewExperiment"

const MAX_REQUESTS = 5_000

export type DataReviewMetricSummary = {
	count: number
	mean: number | null
	p50: number | null
	p95: number | null
	max: number | null
}

export type DataReviewRequestPerformance = {
	requestId: string
	pageId: number
	pageStartTime: number
	pageEndTime: number
	width: number
	queueWaitMs: number | null
	emittedAtMs: number
	firstResponseAtMs: number | null
	completedAtMs: number | null
	timeToFirstResponseMs: number | null
	timeToCompleteMs: number | null
	responseCount: number
	responseBytes: number
	clientProcessingMs: number
	serverRequestElapsedMs: number | null
	serverSetupMs: number | null
	serverDaskBatchElapsedMs: number | null
	nonServerResponseDelayMs: number | null
	workerComputeMs: number
	serializationMs: number
	error: string | null
	cancelled: boolean
}

export type DataReviewPerformanceSnapshot = {
	version: 1
	enabled: boolean
	startedAt: string
	durationMs: number
	configuredPageRequestConcurrency: number
	activePageRequestCount: number
	peakActivePageRequestCount: number
	requestCount: number
	completedRequestCount: number
	completedPageCount: number
	measurementWindowMs: number | null
	completedPagesPerSecond: number | null
	totalResponseBytes: number
	effectiveResponseMbps: number | null
	metrics: {
		queueWaitMs: DataReviewMetricSummary
		timeToFirstResponseMs: DataReviewMetricSummary
		timeToCompleteMs: DataReviewMetricSummary
		clientProcessingMs: DataReviewMetricSummary
		serverRequestElapsedMs: DataReviewMetricSummary
		serverSetupMs: DataReviewMetricSummary
		serverDaskBatchElapsedMs: DataReviewMetricSummary
		nonServerResponseDelayMs: DataReviewMetricSummary
		workerComputeMs: DataReviewMetricSummary
		serializationMs: DataReviewMetricSummary
		responseBytes: DataReviewMetricSummary
	}
	requests: DataReviewRequestPerformance[]
}

type PageQueueTiming = {
	queuedAtMs: number
	loadStartedAtMs: number | null
}

type RequestMetadata = {
	requestId: string
	pageId: number
	pageStartTime: number
	pageEndTime: number
	width: number
}

type DataReviewPerformanceApi = {
	readonly version: 1
	readonly enabled: boolean
	enable(): void
	disable(): void
	reset(): DataReviewPerformanceSnapshot
	snapshot(): DataReviewPerformanceSnapshot
}

declare global {
	interface Window {
		__dataReviewPerformance?: DataReviewPerformanceApi
	}
}

const percentile = (values: number[], probability: number): number | null => {
	if (values.length === 0) {
		return null
	}

	const sorted = [...values].sort((a, b) => a - b)
	const index = (sorted.length - 1) * probability
	const lowerIndex = Math.floor(index)
	const upperIndex = Math.ceil(index)
	const lower = sorted[lowerIndex]
	return lower + (sorted[upperIndex] - lower) * (index - lowerIndex)
}

const summarize = (values: Array<number | null>): DataReviewMetricSummary => {
	const presentValues = values.filter((value): value is number => value !== null)
	return {
		count: presentValues.length,
		mean: presentValues.length === 0
			? null
			: presentValues.reduce((sum, value) => sum + value, 0) / presentValues.length,
		p50: percentile(presentValues, 0.5),
		p95: percentile(presentValues, 0.95),
		max: presentValues.length === 0 ? null : Math.max(...presentValues),
	}
}

class DataReviewPerformanceCollector {
	private isEnabled: boolean
	private startedAtDate: Date = new Date()
	private startedAtMs: number = performance.now()
	private pageQueueTimings = new Map<number, PageQueueTiming>()
	private requests = new Map<string, DataReviewRequestPerformance>()
	private activeRequestIds = new Set<string>()
	private peakActivePageRequestCount = 0

	constructor() {
		const browserWindow = typeof window === "undefined" ? undefined : window
		this.isEnabled = browserWindow !== undefined
			&& new URLSearchParams(browserWindow.location.search).get("dataReviewPerf") === "1"

		if (browserWindow === undefined) {
			return
		}

		browserWindow.__dataReviewPerformance = {
			version: 1,
			get enabled() {
				return dataReviewPerformance.enabled
			},
			enable: () => this.enable(),
			disable: () => this.disable(),
			reset: () => this.reset(),
			snapshot: () => this.snapshot(),
		}
	}

	get enabled(): boolean {
		return this.isEnabled
	}

	enable(): void {
		this.isEnabled = true
	}

	disable(): void {
		this.isEnabled = false
	}

	reset(): DataReviewPerformanceSnapshot {
		this.pageQueueTimings.clear()
		this.requests.clear()
		this.activeRequestIds.clear()
		this.peakActivePageRequestCount = 0
		this.startedAtDate = new Date()
		this.startedAtMs = performance.now()
		return this.snapshot()
	}

	recordPageQueued(pageId: number): void {
		if (!this.isEnabled || this.pageQueueTimings.has(pageId)) {
			return
		}

		this.pageQueueTimings.set(pageId, {
			queuedAtMs: performance.now(),
			loadStartedAtMs: null,
		})
	}

	recordPageLoadStarted(pageId: number): void {
		if (!this.isEnabled) {
			return
		}

		const timing = this.pageQueueTimings.get(pageId)
		if (timing) {
			timing.loadStartedAtMs = performance.now()
		} else {
			const now = performance.now()
			this.pageQueueTimings.set(pageId, { queuedAtMs: now, loadStartedAtMs: now })
		}
	}

	recordRequestEmitted(metadata: RequestMetadata): void {
		if (!this.isEnabled) {
			return
		}

		const emittedAtMs = performance.now()
		const pageTiming = this.pageQueueTimings.get(metadata.pageId)
		const loadStartedAtMs = pageTiming?.loadStartedAtMs ?? emittedAtMs
		this.pageQueueTimings.delete(metadata.pageId)

		this.requests.set(metadata.requestId, {
			...metadata,
			queueWaitMs: pageTiming ? loadStartedAtMs - pageTiming.queuedAtMs : null,
			emittedAtMs,
			firstResponseAtMs: null,
			completedAtMs: null,
			timeToFirstResponseMs: null,
			timeToCompleteMs: null,
			responseCount: 0,
			responseBytes: 0,
			clientProcessingMs: 0,
			serverRequestElapsedMs: null,
			serverSetupMs: null,
			serverDaskBatchElapsedMs: null,
			nonServerResponseDelayMs: null,
			workerComputeMs: 0,
			serializationMs: 0,
			error: null,
			cancelled: false,
		})
		this.activeRequestIds.add(metadata.requestId)
		this.peakActivePageRequestCount = Math.max(this.peakActivePageRequestCount, this.activeRequestIds.size)

		while (this.requests.size > MAX_REQUESTS) {
			const oldestRequestId = this.requests.keys().next().value as string | undefined
			if (oldestRequestId === undefined) {
				break
			}
			this.requests.delete(oldestRequestId)
		}
	}

	recordResponseReceived(response: SocketResponse): void {
		if (!this.isEnabled) {
			return
		}

		const request = this.requests.get(response.request_id)
		if (!request) {
			return
		}

		const receivedAtMs = performance.now()
		if (request.firstResponseAtMs === null) {
			request.firstResponseAtMs = receivedAtMs
			request.timeToFirstResponseMs = receivedAtMs - request.emittedAtMs
		}

		request.responseCount += 1
		request.responseBytes += response.pixels.byteLength

		const serverTiming = response.performance
		if (serverTiming) {
			const nonServerResponseDelayMs = receivedAtMs - request.emittedAtMs - serverTiming.request_elapsed_ms
			request.serverRequestElapsedMs = Math.max(request.serverRequestElapsedMs ?? 0, serverTiming.request_elapsed_ms)
			request.serverSetupMs = Math.max(request.serverSetupMs ?? 0, serverTiming.setup_ms)
			request.serverDaskBatchElapsedMs = Math.max(request.serverDaskBatchElapsedMs ?? 0, serverTiming.dask_batch_elapsed_ms)
			request.nonServerResponseDelayMs = Math.max(request.nonServerResponseDelayMs ?? 0, nonServerResponseDelayMs)
			request.workerComputeMs += serverTiming.worker_compute_ms
			request.serializationMs += serverTiming.serialization_ms
		}
	}

	recordResponseProcessed(requestId: string, processingMs: number, completed: boolean, error?: string): void {
		if (!this.isEnabled) {
			return
		}

		const request = this.requests.get(requestId)
		if (!request) {
			return
		}

		request.clientProcessingMs += processingMs
		request.error = error ?? request.error

		if (completed && request.completedAtMs === null) {
			request.completedAtMs = performance.now()
			request.timeToCompleteMs = request.completedAtMs - request.emittedAtMs
			this.activeRequestIds.delete(requestId)
		}
	}

	recordRequestCancelled(requestId: string): void {
		if (!this.isEnabled) {
			return
		}

		const request = this.requests.get(requestId)
		if (request) {
			request.cancelled = true
			this.activeRequestIds.delete(requestId)
		}
	}

	snapshot(): DataReviewPerformanceSnapshot {
		const requests = [...this.requests.values()].map(request => ({ ...request }))
		const completedRequests = requests.filter(request => request.completedAtMs !== null)
		const completedPageCount = new Set(completedRequests.map(request => request.pageId)).size
		const firstEmittedAtMs = completedRequests.length === 0
			? null
			: Math.min(...completedRequests.map(request => request.emittedAtMs))
		const lastCompletedAtMs = completedRequests.length === 0
			? null
			: Math.max(...completedRequests.map(request => request.completedAtMs as number))
		const measurementWindowMs = firstEmittedAtMs === null || lastCompletedAtMs === null
			? null
			: lastCompletedAtMs - firstEmittedAtMs
		const totalResponseBytes = requests.reduce((total, request) => total + request.responseBytes, 0)
		return {
			version: 1,
			enabled: this.isEnabled,
			startedAt: this.startedAtDate.toISOString(),
			durationMs: performance.now() - this.startedAtMs,
			configuredPageRequestConcurrency: getDataReviewPageRequestConcurrency(),
			activePageRequestCount: this.activeRequestIds.size,
			peakActivePageRequestCount: this.peakActivePageRequestCount,
			requestCount: requests.length,
			completedRequestCount: completedRequests.length,
			completedPageCount,
			measurementWindowMs,
			completedPagesPerSecond: measurementWindowMs && measurementWindowMs > 0
				? completedPageCount / (measurementWindowMs / 1_000)
				: null,
			totalResponseBytes,
			effectiveResponseMbps: measurementWindowMs && measurementWindowMs > 0
				? totalResponseBytes * 8 / (measurementWindowMs * 1_000)
				: null,
			metrics: {
				queueWaitMs: summarize(requests.map(request => request.queueWaitMs)),
				timeToFirstResponseMs: summarize(requests.map(request => request.timeToFirstResponseMs)),
				timeToCompleteMs: summarize(requests.map(request => request.timeToCompleteMs)),
				clientProcessingMs: summarize(requests.map(request => request.clientProcessingMs)),
				serverRequestElapsedMs: summarize(requests.map(request => request.serverRequestElapsedMs)),
				serverSetupMs: summarize(requests.map(request => request.serverSetupMs)),
				serverDaskBatchElapsedMs: summarize(requests.map(request => request.serverDaskBatchElapsedMs)),
				nonServerResponseDelayMs: summarize(requests.map(request => request.nonServerResponseDelayMs)),
				workerComputeMs: summarize(requests.map(request => request.workerComputeMs)),
				serializationMs: summarize(requests.map(request => request.serializationMs)),
				responseBytes: summarize(requests.map(request => request.responseBytes)),
			},
			requests,
		}
	}
}

export const dataReviewPerformance = new DataReviewPerformanceCollector()
